import React, { useState } from 'react';
import { ComponentSkinProps } from '../../../../types/component-skin';
import { EditedUserInfo } from '../UserProfile.types';

/**
 * 사용자 프로필 기본 스킨
 */
const BasicUserProfileSkin: React.FC<ComponentSkinProps> = ({ 
  data, 
  actions, 
  options, 
  mode, 
  utils,
  app,
  editor
}) => {
  const { t } = utils;
  
  // 복사 상태
  const [copied, setCopied] = useState(false);
  
  // 데이터 추출 및 기본값 설정
  const { 
    userInfo = null,
    editedInfo = {},
    loading = false,
    errors = {},
    showPasswordFields = false,
    isUserLoggedIn = false,
    isAdminMode = false,
    varFields = {},
    theme = {},
    isEditing = false,
    saveError = null,
    highlightedFields = {},
    referralLink = '',
    withcookieData = {}
  } = data || {};
  
  // 액션 추출 및 기본값 설정
  const {
    handleFieldChange = () => {},
    handleUpdate = () => {},
    togglePasswordChange = () => {},
    handleEdit = () => {},
    handleCancel = () => {},
    handleSave = () => {},
    copyToClipboard = () => {}
  } = actions || {};
  
  // 옵션 추출 및 기본값 설정
  const {
    title = t('회원 정보'),
    content = {},
    style = {},
    basicFields = {
      userId: true,
      name: true,
      password: true,
      phone: true,
      email: true,
      birthday: true,
      createdAt: true,
      address: true,
      referralLink: true
    }
  } = options || {};
  
  // 테마 색상 가져오기
  const colorSet = theme?.colorset || {};
  const primaryColor = colorSet?.primary || "#007bff";
  const secondaryColor = colorSet?.secondary || "#6c757d";
  const successColor = colorSet?.tertiary || "#28a745";
  
  // 스타일 설정
  const defaultStyle: React.CSSProperties = {
    padding: '20px',
    backgroundColor: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
    fontFamily: 'Noto Sans KR, sans-serif',
    maxWidth: '800px',
    margin: '20px auto',
    position: 'relative',
    ...style
  };
  
  // 프로필 정보 행 스타일
  const profileRowStyle = {
    display: 'flex',
    flexWrap: 'wrap' as const,
    gap: '15px',
    marginBottom: '15px',
    width: '100%',
    borderBottom: '1px solid #eee',
    paddingBottom: '15px'
  };

  // 프로필 라벨 스타일
  const labelStyle = {
    flex: '0 0 120px',
    fontWeight: 'bold' as const,
    color: '#555'
  };

  // 프로필 값 스타일
  const valueStyle = {
    flex: '1 1 calc(100% - 135px)'
  };

  // 하이라이트된 값 스타일
  const getHighlightedStyle = (field: string) => {
    return highlightedFields[field] ? {
      backgroundColor: '#e6f7ff',
      padding: '5px',
      borderRadius: '4px',
      transition: 'background-color 0.5s ease',
      animation: 'fadeHighlight 3s'
    } : {};
  };

  // 입력 필드 스타일
  const inputStyle = {
    width: '100%',
    padding: '8px 10px',
    border: '1px solid #ddd',
    borderRadius: '4px',
    fontSize: '14px'
  };
  
  const errorMessageStyle = {
    color: '#dc3545',
    marginTop: '10px',
    fontSize: '14px',
    textAlign: 'center' as const
  };
  
  // 버튼 컨테이너 스타일
  const buttonContainerStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginBottom: '20px'
  };

  // 수정 버튼 스타일
  const editButtonStyle = {
    padding: '8px 16px',
    backgroundColor: primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    transition: 'background-color 0.2s'
  };

  // 취소 버튼 스타일
  const cancelButtonStyle = {
    padding: '8px 16px',
    backgroundColor: secondaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    transition: 'background-color 0.2s'
  };

  // 저장 버튼 스타일
  const saveButtonStyle = {
    padding: '8px 16px',
    backgroundColor: successColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    transition: 'background-color 0.2s',
    opacity: loading ? 0.7 : 1
  };
  
  // 추천링크 스타일
  const referralBoxStyle = {
    marginTop: '30px',
    padding: '15px',
    backgroundColor: '#f8f9fa',
    borderRadius: '5px',
    border: '1px solid #ddd'
  };

  // 추천링크 헤더 스타일
  const referralHeaderStyle = {
    fontSize: '16px',
    fontWeight: 'bold' as const,
    marginBottom: '10px',
    color: '#333'
  };

  // 추천링크 인풋 스타일
  const referralInputStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  };

  // 추천링크 텍스트 필드 스타일
  const linkFieldStyle = {
    flex: '1',
    padding: '10px',
    border: '1px solid #ced4da',
    borderRadius: '4px',
    fontSize: '14px',
    backgroundColor: '#f1f3f5',
    color: '#495057',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap' as const
  };

  // 복사 버튼 스타일
  const copyButtonStyle = {
    padding: '10px 15px',
    backgroundColor: copied ? successColor : primaryColor,
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 'bold' as const,
    transition: 'background-color 0.2s'
  };
  
  // 생년월일 포맷팅 함수
  const formatBirthdate = (date: string | Date | undefined) => {
    if (!date) return '-';
    
    // 날짜 문자열인 경우 처리
    if (typeof date === 'string') {
      // ISO 날짜 형식(YYYY-MM-DDTHH:mm:ss.sssZ)인 경우 날짜 부분만 추출
      if (date.includes('T')) {
        return date.split('T')[0];
      }
      
      // 이미 YYYY-MM-DD 형식이면 그대로 반환
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      
      // 다른 형식의 날짜 문자열인 경우 Date 객체로 변환 후 처리
      try {
        const dateObj = new Date(date);
        if (isNaN(dateObj.getTime())) return t('Invalid date'); // 유효하지 않은 날짜면 원본 반환
        return dateObj.toISOString().split('T')[0];
      } catch (e) {
        return date; // 변환 실패시 원본 반환
      }
    }
    
    // Date 객체인 경우 처리
    if (date instanceof Date) {
      return date.toISOString().split('T')[0];
    }
    
    return String(date); // 다른 타입이면 문자열로 변환하여 반환
  };
  
  // 클립보드에 복사 기능
  const handleCopyToClipboard = () => {
    if (copyToClipboard) {
      copyToClipboard();
      setCopied(true);
      setTimeout(() => setCopied(false), 3000); // 3초 후 메시지 숨김
    }
  };
  
  // 로그인되지 않은 경우
  if (!isUserLoggedIn && !isAdminMode) {
    return (
      <div style={defaultStyle}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center' }}>{t(content.title || title)}</h2>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>{t('로그인이 필요합니다.')}</p>
          <button 
            onClick={() => utils.navigate && utils.navigate('/login')}
            style={{
              marginTop: '10px',
              padding: '8px 16px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            {t('다시 시도')}
          </button>
        </div>
      </div>
    );
  }
  
  // 사용자 정보가 없는 경우
  if (!userInfo && !editedInfo.name) {
    return (
      <div style={defaultStyle}>
        <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center' }}>{t(content.title || title)}</h2>
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <p>{t('회원 정보를 불러오는 중...')}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div style={defaultStyle}>
      {loading && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(255, 255, 255, 0.7)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 10,
          borderRadius: '8px'
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ marginBottom: '10px', fontSize: '20px' }}>
              {loading ? t('저장 중...') : t('정보 로드 중...')}
            </div>
            <div>{t('잠시만 기다려주세요.')}</div>
          </div>
        </div>
      )}
      
      <h2 style={{ marginTop: 0, marginBottom: '20px', textAlign: 'center' }}>{t(content.title || title)}</h2>
      
      {/* 수정/저장 버튼 */}
      <div style={buttonContainerStyle}>
        {isEditing ? (
          <>
            <button 
              onClick={handleCancel} 
              style={cancelButtonStyle}
              disabled={loading}
            >
              {t('취소')}
            </button>
            <button 
              onClick={handleSave} 
              style={saveButtonStyle}
              disabled={loading}
            >
              {loading ? t('저장 중...') : t('저장')}
            </button>
          </>
        ) : (
          <button 
            onClick={handleEdit} 
            style={editButtonStyle}
            disabled={loading}
          >
            {t('수정')}
          </button>
        )}
      </div>
      
      {/* 에러 메시지 */}
      {saveError && (
        <div style={errorMessageStyle}>{saveError}</div>
      )}
      
      {/* 유저 정보 섹션 */}
      <div style={{ marginBottom: '30px' }}>
        {/* 아이디 */}
        {basicFields.userId && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('아이디')}</div>
            <div style={valueStyle}>{userInfo?.insungId || userInfo?.id || '-'}</div>
          </div>
        )}
        
        {/* 이름 */}
        {basicFields.name && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('이름')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="text"
                  style={inputStyle}
                  value={editedInfo.name || ''}
                  onChange={(e) => handleFieldChange('name', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('name')}>{userInfo?.name || '-'}</div>
              )}
            </div>
          </div>
        )}
        
        {/* 비밀번호 */}
        {basicFields.password && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('비밀번호')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="password"
                  style={inputStyle}
                  placeholder={t('새 비밀번호 입력 (변경하지 않으려면 빈칸으로 두세요)')}
                  onChange={(e) => handleFieldChange('password', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('password')}>••••••••</div>
              )}
            </div>
          </div>
        )}
        
        {/* 전화번호 */}
        {basicFields.phone && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('전화번호')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="tel"
                  style={inputStyle}
                  value={editedInfo.phone || ''}
                  onChange={(e) => handleFieldChange('phone', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('phone')}>{userInfo?.phone || '-'}</div>
              )}
            </div>
          </div>
        )}
        
        {/* 이메일 */}
        {basicFields.email && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('이메일')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="email"
                  style={inputStyle}
                  value={editedInfo.email || ''}
                  onChange={(e) => handleFieldChange('email', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('email')}>{userInfo?.email || '-'}</div>
              )}
            </div>
          </div>
        )}
        
        {/* 생년월일 */}
        {basicFields.birthday && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('생년월일')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="date"
                  style={inputStyle}
                  value={editedInfo.birthday || ''}
                  onChange={(e) => handleFieldChange('birthday', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('birthday')}>
                  {formatBirthdate(userInfo?.birthdate || userInfo?.birthday)}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* 가입일 */}
        {basicFields.createdAt && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('가입일')}</div>
            <div style={valueStyle}>
              {userInfo?.created_at ? new Date(userInfo.created_at).toLocaleDateString('ko-KR') : '-'}
            </div>
          </div>
        )}
        
        {/* 주소 */}
        {basicFields.address && (
          <div style={profileRowStyle}>
            <div style={labelStyle}>{t('주소')}</div>
            <div style={valueStyle}>
              {isEditing ? (
                <input
                  type="text"
                  style={inputStyle}
                  value={editedInfo.address || ''}
                  onChange={(e) => handleFieldChange('address', e.target.value)}
                />
              ) : (
                <div style={getHighlightedStyle('address')}>{userInfo?.address || '-'}</div>
              )}
            </div>
          </div>
        )}
        
        {/* 커스텀 필드들 */}
        {Object.keys(varFields).map((key) => {
          const field = varFields[key];
          if (!field.show) return null;
          
          const fieldValue = editedInfo[key as keyof EditedUserInfo] || userInfo?.[key] || '';
          
          return (
            <div key={key} style={profileRowStyle}>
              <div style={labelStyle}>{t(field.label)}</div>
              <div style={valueStyle}>
                {isEditing ? (
                  <input
                    type="text"
                    style={inputStyle}
                    value={editedInfo[key as keyof EditedUserInfo] || ''}
                    onChange={(e) => handleFieldChange(key as keyof EditedUserInfo, e.target.value)}
                  />
                ) : (
                  <div style={getHighlightedStyle(key)}>
                    {userInfo?.[key] || '-'}
                  </div>
                )}
              </div>
            </div>
          );
        })}
        
        {/* 지갑 주소 필드 (companyId === 24 일 경우만 표시) */}
        {(withcookieData?.skin?.companyId === 24 || withcookieData?.skin?.companyId === 26 || withcookieData?.skin?.companyId === 27) && (
          <>
            {(withcookieData?.skin?.companyId === 24) && (
              <>
                {/* TRX 지갑 주소 */}
                <div style={profileRowStyle}>
                  <div style={labelStyle}>{t('TRX 지갑주소')}</div>
                  <div style={valueStyle}>
                    {isEditing ? (
                      <>
                        <input
                          type="text"
                          style={{
                            ...inputStyle,
                            backgroundColor: userInfo?.trxWithdrawAddress ? '#f5f5f5' : '#ffffff',
                            cursor: userInfo?.trxWithdrawAddress ? 'not-allowed' : 'text'
                          }}
                          value={editedInfo.trxWithdrawAddress || ''}
                          onChange={(e) => handleFieldChange('trxWithdrawAddress', e.target.value)}
                          disabled={userInfo?.trxWithdrawAddress ? true : false}
                          placeholder={userInfo?.trxWithdrawAddress ? t('이미 설정된 주소는 수정할 수 없습니다') : t('TRX 지갑주소 입력')}
                        />
                        {userInfo?.trxWithdrawAddress && (
                          <div style={{color: '#ff9800', fontSize: '12px', marginTop: '5px'}}>
                            {t('TRX 주소는 이미 설정되어 수정할 수 없습니다.')}
                          </div>
                        )}
                      </>
                    ) : (
                      <div style={getHighlightedStyle('trxWithdrawAddress')}>
                        {userInfo?.trxWithdrawAddress || '-'}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            {/* BSC 지갑 주소 */}
            <div style={profileRowStyle}>
              <div style={labelStyle}>{t('USDT-BNB 지갑주소')}</div>
              <div style={valueStyle}>
                {isEditing ? (
                  <>
                    <input
                      type="text"
                      style={{
                        ...inputStyle,
                        backgroundColor: userInfo?.bscWithdrawAddress ? '#f5f5f5' : '#ffffff',
                        cursor: userInfo?.bscWithdrawAddress ? 'not-allowed' : 'text'
                      }}
                      value={editedInfo.bscWithdrawAddress || ''}
                      onChange={(e) => handleFieldChange('bscWithdrawAddress', e.target.value)}
                      disabled={userInfo?.bscWithdrawAddress ? true : false}
                      placeholder={userInfo?.bscWithdrawAddress ? t('이미 설정된 주소는 수정할 수 없습니다') : t('USDT-BNB 지갑주소 입력')}
                    />
                    {userInfo?.bscWithdrawAddress && (
                      <div style={{color: '#ff9800', fontSize: '12px', marginTop: '5px'}}>
                        {t('BSC 주소는 이미 설정되어 수정할 수 없습니다.')}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={getHighlightedStyle('bscWithdrawAddress')}>
                    {userInfo?.bscWithdrawAddress || '-'}
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      
      {/* 추천링크 박스 */}
      {basicFields.referralLink && referralLink && (
        <div style={referralBoxStyle}>
          <div style={referralHeaderStyle}>{t('추천링크')}</div>
          <div style={referralInputStyle}>
            <div style={linkFieldStyle}>{referralLink}</div>
            <button 
              onClick={handleCopyToClipboard} 
              style={copyButtonStyle}
              title={t('클립보드에 링크 복사')}
            >
              {copied ? t('복사됨!') : t('복사')}
            </button>
          </div>
          {copied && (
            <div style={{ color: 'green', fontSize: '12px', marginTop: '5px' }}>
              {t('클립보드에 복사되었습니다!')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BasicUserProfileSkin;